import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Phone, Loader2, Plus, Trash2, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import {
  fetchVoicemailDrops,
  saveVoicemailDrop,
  deleteVoicemailDrop,
  fetchDidPool,
  addDid,
  deactivateDid,
  type VoicemailDropRecording,
  type TenantDidPool,
} from '@/lib/powerDialer';

// -------------------------------------------------------------------------
// PowerDialerAdminPage — Stories 69.1 + 69.2
// Route: /admin/power-dialer
// -------------------------------------------------------------------------

type Tab = 'voicemail' | 'dids';

export function PowerDialerAdminPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>('voicemail');
  const [drops, setDrops] = useState<VoicemailDropRecording[]>([]);
  const [dids, setDids] = useState<TenantDidPool[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [d, p] = await Promise.all([fetchVoicemailDrops(), fetchDidPool()]);
        if (!cancelled) {
          setDrops(d);
          setDids(p);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <Phone className="h-6 w-6" />
          Power Dialer v2
        </h1>
      </div>

      <div className="flex gap-4 mb-6 border-b">
        <TabButton active={tab === 'voicemail'} onClick={() => setTab('voicemail')}>
          {t('voicemailDrop.title')}
        </TabButton>
        <TabButton active={tab === 'dids'} onClick={() => setTab('dids')}>
          {t('didPool.title')}
        </TabButton>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" />
          {t('common.loading')}
        </div>
      ) : tab === 'voicemail' ? (
        <VoicemailDropsTab drops={drops} onDropsChange={setDrops} />
      ) : (
        <DidPoolTab dids={dids} onDidsChange={setDids} />
      )}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors ${
        active
          ? 'border-primary text-primary'
          : 'border-transparent text-muted-foreground hover:text-foreground'
      }`}
    >
      {children}
    </button>
  );
}

// -------------------------------------------------------------------------
// Voicemail Drops Tab (Story 69.1)
// -------------------------------------------------------------------------

interface VoicemailDropsTabProps {
  drops: VoicemailDropRecording[];
  onDropsChange: (d: VoicemailDropRecording[]) => void;
}

function VoicemailDropsTab({ drops, onDropsChange }: VoicemailDropsTabProps) {
  const { t } = useTranslation();
  const [showAdd, setShowAdd] = useState(false);

  async function handleDelete(id: string) {
    await deleteVoicemailDrop(id);
    onDropsChange(drops.filter((d) => d.id !== id));
  }

  return (
    <>
      <p className="text-muted-foreground text-sm mb-4">{t('voicemailDrop.subtitle')}</p>
      <div className="flex justify-end mb-4">
        <Button onClick={() => setShowAdd(true)}>
          <Plus className="h-4 w-4 mr-1" />
          {t('voicemailDrop.btn_add')}
        </Button>
      </div>

      {drops.length === 0 ? (
        <p className="text-center text-muted-foreground py-10">{t('voicemailDrop.empty')}</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-muted-foreground text-left">
              <th className="pb-2 pr-4 font-medium">{t('voicemailDrop.col_name')}</th>
              <th className="pb-2 pr-4 font-medium">{t('voicemailDrop.col_duration')}</th>
              <th className="pb-2 font-medium" />
            </tr>
          </thead>
          <tbody>
            {drops.map((d) => (
              <tr key={d.id} className="border-b last:border-0 hover:bg-muted/30">
                <td className="py-2 pr-4 font-medium">{d.name}</td>
                <td className="py-2 pr-4 text-muted-foreground">
                  {d.durationSeconds != null ? `${d.durationSeconds}s` : '—'}
                </td>
                <td className="py-2 text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(d.id)}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-3.5 w-3.5 mr-1" />
                    {t('voicemailDrop.btn_delete')}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showAdd && (
        <AddVoicemailDropDialog
          onClose={() => setShowAdd(false)}
          onCreate={(r) => {
            onDropsChange([r, ...drops]);
            setShowAdd(false);
          }}
        />
      )}
    </>
  );
}

interface AddVoicemailDropDialogProps {
  onClose: () => void;
  onCreate: (r: VoicemailDropRecording) => void;
}

function AddVoicemailDropDialog({ onClose, onCreate }: AddVoicemailDropDialogProps) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [audioUrl, setAudioUrl] = useState('');
  const [duration, setDuration] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit() {
    if (!name.trim() || !audioUrl.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const r = await saveVoicemailDrop(
        name,
        audioUrl,
        duration ? parseInt(duration, 10) : undefined,
      );
      onCreate(r);
    } catch {
      setError(t('voicemailDrop.add_error'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-background rounded-lg shadow-xl p-6 w-full max-w-md">
        <h2 className="text-lg font-semibold mb-4">{t('voicemailDrop.add_dialog_title')}</h2>

        <label className="block text-sm font-medium mb-1">{t('voicemailDrop.add_name_label')}</label>
        <input
          className="w-full border rounded px-3 py-2 text-sm mb-3"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />

        <label className="block text-sm font-medium mb-1">{t('voicemailDrop.add_url_label')}</label>
        <input
          className="w-full border rounded px-3 py-2 text-sm mb-3"
          value={audioUrl}
          onChange={(e) => setAudioUrl(e.target.value)}
          placeholder="https://storage.example.com/voicemail.mp3"
        />

        <label className="block text-sm font-medium mb-1">
          {t('voicemailDrop.add_duration_label')}
        </label>
        <input
          className="w-full border rounded px-3 py-2 text-sm mb-4"
          type="number"
          min="1"
          value={duration}
          onChange={(e) => setDuration(e.target.value)}
        />

        {error && <p className="text-destructive text-sm mb-3">{error}</p>}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={submitting || !name.trim() || !audioUrl.trim()}>
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
                {t('voicemailDrop.add_submitting')}
              </>
            ) : (
              t('voicemailDrop.add_submit')
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------------
// DID Pool Tab (Story 69.2)
// -------------------------------------------------------------------------

interface DidPoolTabProps {
  dids: TenantDidPool[];
  onDidsChange: (d: TenantDidPool[]) => void;
}

function DidPoolTab({ dids, onDidsChange }: DidPoolTabProps) {
  const { t } = useTranslation();
  const [showAdd, setShowAdd] = useState(false);

  async function handleDeactivate(id: string) {
    if (!window.confirm(t('didPool.deactivate_confirm', { number: dids.find((d) => d.id === id)?.phoneNumber }))) return;
    await deactivateDid(id);
    onDidsChange(dids.map((d) => (d.id === id ? { ...d, isActive: false } : d)));
  }

  return (
    <>
      <p className="text-muted-foreground text-sm mb-4">{t('didPool.subtitle')}</p>
      <div className="flex justify-end mb-4">
        <Button onClick={() => setShowAdd(true)}>
          <Plus className="h-4 w-4 mr-1" />
          {t('didPool.btn_add')}
        </Button>
      </div>

      {dids.length === 0 ? (
        <p className="text-center text-muted-foreground py-10">{t('didPool.empty')}</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-muted-foreground text-left">
              <th className="pb-2 pr-4 font-medium">{t('didPool.col_country')}</th>
              <th className="pb-2 pr-4 font-medium">{t('didPool.col_number')}</th>
              <th className="pb-2 pr-4 font-medium">{t('didPool.col_provider')}</th>
              <th className="pb-2 pr-4 font-medium">{t('common.status')}</th>
              <th className="pb-2 font-medium" />
            </tr>
          </thead>
          <tbody>
            {dids.map((d) => (
              <tr key={d.id} className="border-b last:border-0 hover:bg-muted/30">
                <td className="py-2 pr-4">
                  <span className="inline-flex items-center gap-1">
                    <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                    {d.countryCode}
                  </span>
                </td>
                <td className="py-2 pr-4 font-mono">{d.phoneNumber}</td>
                <td className="py-2 pr-4 text-muted-foreground">{d.provider}</td>
                <td className="py-2 pr-4">
                  <span
                    className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
                      d.isActive
                        ? 'bg-green-900/40 text-green-300'
                        : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {d.isActive ? t('common.active') : t('common.closed')}
                  </span>
                </td>
                <td className="py-2 text-right">
                  {d.isActive && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeactivate(d.id)}
                      className="text-destructive hover:text-destructive"
                    >
                      {t('didPool.btn_deactivate')}
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showAdd && (
        <AddDidDialog
          onClose={() => setShowAdd(false)}
          onCreate={(did) => {
            onDidsChange([...dids, did]);
            setShowAdd(false);
          }}
        />
      )}
    </>
  );
}

interface AddDidDialogProps {
  onClose: () => void;
  onCreate: (d: TenantDidPool) => void;
}

function AddDidDialog({ onClose, onCreate }: AddDidDialogProps) {
  const { t } = useTranslation();
  const [countryCode, setCountryCode] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [provider, setProvider] = useState('TWILIO');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit() {
    if (!countryCode.trim() || !phoneNumber.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const d = await addDid(countryCode, phoneNumber, provider);
      onCreate(d);
    } catch {
      setError(t('didPool.add_error'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-background rounded-lg shadow-xl p-6 w-full max-w-md">
        <h2 className="text-lg font-semibold mb-4">{t('didPool.add_dialog_title')}</h2>

        <label className="block text-sm font-medium mb-1">{t('didPool.add_country_label')}</label>
        <input
          className="w-full border rounded px-3 py-2 text-sm mb-3 uppercase"
          value={countryCode}
          onChange={(e) => setCountryCode(e.target.value.toUpperCase())}
          maxLength={2}
          placeholder="CO"
        />

        <label className="block text-sm font-medium mb-1">{t('didPool.add_number_label')}</label>
        <input
          className="w-full border rounded px-3 py-2 text-sm mb-3 font-mono"
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value)}
          placeholder="+573001234567"
        />

        <label className="block text-sm font-medium mb-1">{t('didPool.add_provider_label')}</label>
        <select
          className="w-full border rounded px-3 py-2 text-sm mb-4"
          value={provider}
          onChange={(e) => setProvider(e.target.value)}
        >
          <option value="TWILIO">Twilio</option>
          <option value="VONAGE">Vonage</option>
          <option value="TELNYX">Telnyx</option>
          <option value="STUB">Stub (dev)</option>
        </select>

        {error && <p className="text-destructive text-sm mb-3">{error}</p>}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || !countryCode.trim() || !phoneNumber.trim()}
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
                {t('didPool.add_submitting')}
              </>
            ) : (
              t('didPool.add_submit')
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
